import { EventCustomAttributes } from './eventTypeConstantsAmplitude';
import { amplitudeSendEvent } from './amplitude';
import React, { useContext } from 'react';

const AnalyticsCustomAttributesContext = React.createContext<EventCustomAttributes>({});

export interface AmplitudeSendEventProps {
  amplitudeSendEvent(event: string, data?: EventCustomAttributes): void;
}

interface AddAnalyticsCustomAttributeProps {
  value: EventCustomAttributes;
}

export const AddAnalyticsCustomAttribute: React.FC<AddAnalyticsCustomAttributeProps> = ({
  value,
  children,
}) => {
  const previousValue = useContext(AnalyticsCustomAttributesContext);
  const newValue = {
    ...previousValue,
    ...value,
  };
  return (
    <AnalyticsCustomAttributesContext.Provider value={newValue}>
      {children}
    </AnalyticsCustomAttributesContext.Provider>
  );
};

/**
 * HOC wrapper to provide an amplitude send event function that hooks into the above context.
 */
export function withAmplitudeSendEvent<P>(
  Component: React.ComponentType<React.PropsWithChildren<P & AmplitudeSendEventProps>>
) {
  const WithAmplitudeSendEvent = (props: P) => {
    const amplitudeSendEventInternal = useAmplitudeSendEvent();
    // The any comes from a bad interaction between emotion and react types.
    // See: https://github.com/emotion-js/emotion/issues/2169
    /* eslint-disable  @typescript-eslint/no-explicit-any */
    return <Component amplitudeSendEvent={amplitudeSendEventInternal} {...(props as any)} />;
  };

  const componentName = Component.displayName || Component.name || 'Component';
  WithAmplitudeSendEvent.displayName = `WithAmplitudeSendEvent(${componentName})`;

  return WithAmplitudeSendEvent;
}

export const useAmplitudeSendEvent = () => {
  const wrapperAttributes = useContext(AnalyticsCustomAttributesContext);

  return (event: string, data?: EventCustomAttributes) => {
    amplitudeSendEvent(event, { ...wrapperAttributes, ...data });
  };
};
